import type { FeedbackInline, FeedbackWidgetConfig, WidgetStep } from '../types.js';
import { normalizeRatingStyle } from '../types.js';

export function buildSteps(
  inline: FeedbackInline | null,
  widgetConfig: FeedbackWidgetConfig | null,
): WidgetStep[] {
  // Server-defined steps[] take precedence; otherwise derive from flat config.
  const inlineWithSteps = inline as (FeedbackInline & { steps?: WidgetStep[] }) | null;
  if (inlineWithSteps?.steps && inlineWithSteps.steps.length > 0) {
    return appendThanksFromInline(inlineWithSteps);
  }
  if (inline) return stepsFromInlineConfig(inline);
  if (widgetConfig?.steps && widgetConfig.steps.length > 0) {
    return appendThanksStep(widgetConfig);
  }
  return DEFAULT_STEPS;
}

export function shouldSkipIdentity(
  step: WidgetStep,
  knownEmail: string | undefined,
  knownFullName: string | undefined,
): boolean {
  if (step.kind !== 'identity') return false;
  const wantsEmail = step.askEmail ?? true;
  const wantsName = step.askName ?? false;
  if (wantsEmail && !knownEmail) return false;
  if (wantsName && !knownFullName) return false;
  return true;
}

export function resolveHeaderTitle(
  step: WidgetStep | undefined,
  inline: FeedbackInline | null,
): string {
  if (inline?.title) return inline.title;
  switch (step?.kind) {
    case 'type-picker':
      return 'Share feedback';
    case 'rating':
      return 'Rate us';
    case 'comment':
      return 'Tell us more';
    case 'identity':
      return 'Stay in touch';
    default:
      return 'Feedback';
  }
}

function stepsFromInlineConfig(inline: FeedbackInline): WidgetStep[] {
  const steps: WidgetStep[] = [];
  // 'full' (SDK) and 'both' (dashboard) both require name + email.
  const identityFull = inline.identity === 'full' || inline.identity === 'both';
  const attachTypes = (inline as FeedbackInline & { attachTypes?: string[] }).attachTypes;
  if (inline.rating !== false) {
    steps.push({
      kind: 'rating',
      question: inline.title ?? 'How would you rate us?',
      ratingStyle: normalizeRatingStyle(inline.rating),
      required: true,
    });
  }
  if (inline.comment !== false || inline.attachments !== false) {
    steps.push({
      kind: 'comment',
      question: inline.rating === false ? (inline.title ?? 'What would you like to share?') : 'Tell us more',
      placeholder: inline.labels?.placeholder ?? 'Add more details (optional)',
      // Quick-rating flows show the screenshot/recording row by default so
      // respondents can add evidence without the host app having to set
      // `attachments: true` on every inline call. Opt out with
      // `attachments: false`.
      allowAttachments: inline.attachments !== false,
      attachTypes,
    });
  }
  if (inline.identity && inline.identity !== 'none') {
    steps.push({
      kind: 'identity',
      question: 'How can we reach you?',
      askEmail: true,
      askName: identityFull,
      identityRequired: identityFull ? 'both' : 'email',
    });
  }
  // Structured `thanks` overrides the single-string `labels.thanks`.
  const thanks = (inline as FeedbackInline & {
    thanks?: { title?: string; body?: string; imageUrl?: string; ctaLabel?: string; ctaUrl?: string };
  }).thanks;
  steps.push({
    kind: 'thanks',
    thanksTitle: thanks?.title ?? inline.labels?.thanks,
    thanksBody: thanks?.body,
    thanksImageUrl: thanks?.imageUrl,
    thanksCtaLabel: thanks?.ctaLabel,
    thanksCtaUrl: thanks?.ctaUrl,
  });
  return steps;
}

function appendThanksFromInline(inline: FeedbackInline & { steps?: WidgetStep[] }): WidgetStep[] {
  const configured = [...(inline.steps ?? [])];
  if (!configured.some((step) => step.kind === 'thanks')) {
    const thanks = (inline as FeedbackInline & {
      thanks?: { title?: string; body?: string; imageUrl?: string; ctaLabel?: string; ctaUrl?: string };
    }).thanks;
    configured.push({
      kind: 'thanks',
      thanksTitle: thanks?.title ?? inline.labels?.thanks,
      thanksBody: thanks?.body,
      thanksImageUrl: thanks?.imageUrl,
      thanksCtaLabel: thanks?.ctaLabel,
      thanksCtaUrl: thanks?.ctaUrl,
    });
  }
  return configured;
}

function appendThanksStep(widgetConfig: FeedbackWidgetConfig): WidgetStep[] {
  const configured = [...(widgetConfig.steps ?? [])];
  if (!configured.some((step) => step.kind === 'thanks')) {
    configured.push({
      kind: 'thanks',
      thanksTitle: widgetConfig.labels?.thanksTitle,
      thanksBody: widgetConfig.labels?.thanksBody,
    });
  }
  return configured;
}

const DEFAULT_STEPS: WidgetStep[] = [
  { kind: 'type-picker', question: "What's on your mind?", required: true },
  { kind: 'rating', question: "How's it going?", ratingStyle: 'emoji-5', required: true },
  {
    kind: 'comment',
    question: 'Tell us more',
    placeholder: 'Add more details (optional)',
    allowAttachments: true,
  },
  { kind: 'thanks' },
];
